import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
} from 'chart.js';
import 'chartjs-adapter-date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

interface TimelineData {
  time_bucket: string;
  comment_count: number;
}

interface CommentTimelineProps {
  selectedVideo: string;
}

const CHART_STYLES = {
  colors: {
    primary: 'rgb(139, 92, 246)',
    primaryAlpha: 'rgba(139, 92, 246, 0.15)',
    text: 'rgba(255, 255, 255, 0.9)',
    textDim: 'rgba(255, 255, 255, 0.7)',
    grid: 'rgba(255, 255, 255, 0.1)'
  },
  fonts: {
    normal: { size: 12 },
    large: { size: 14 },
    tooltip: { size: 13 }
  }
};

const getChartOptions = () => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    intersect: true,
    mode: 'nearest' as const
  },
  scales: {
    x: {
      type: 'time' as const,
      time: {
        unit: 'week' as const,
        displayFormats: {
          week: 'MMM D'
        },
        tooltipFormat: 'PP'
      },
      grid: {
        display: true,
        color: CHART_STYLES.colors.grid
      },
      title: {
        display: true,
        text: 'Date',
        color: CHART_STYLES.colors.textDim,
        font: CHART_STYLES.fonts.large
      },
      ticks: {
        color: CHART_STYLES.colors.text,
        maxRotation: 45,
        minRotation: 45,
        font: CHART_STYLES.fonts.normal,
        maxTicksLimit: 8,
        padding: 8,
        callback: (value: any) => {
          const date = new Date(value);
          return date.toLocaleDateString('en-US', { 
            month: 'short',
            day: 'numeric'
          });
        }
      }
    },
    y: {
      beginAtZero: true,
      grid: {
        display: true,
        color: CHART_STYLES.colors.grid
      },
      title: {
        display: true,
        text: 'Total Comments per Day',
        color: CHART_STYLES.colors.textDim,
        font: CHART_STYLES.fonts.large
      },
      ticks: {
        color: CHART_STYLES.colors.text,
        precision: 0,
        stepSize: 5,
        font: CHART_STYLES.fonts.normal,
        padding: 8
      }
    }
  },
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: CHART_STYLES.colors.text,
      bodyColor: CHART_STYLES.colors.text,
      padding: 12,
      displayColors: false,
      titleFont: CHART_STYLES.fonts.large,
      bodyFont: CHART_STYLES.fonts.tooltip,
      callbacks: {
        title: (context: any) => {
          const date = new Date(context[0].parsed.x);
          return date.toLocaleDateString('en-US', { 
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
        },
        label: (context: any) => {
          const count = context.parsed.y;
          return `${count} total comment${count === 1 ? '' : 's'}`;
        }
      }
    }
  }
});

export function CommentTimeline({ selectedVideo }: CommentTimelineProps) {
  const { data: timelineData, isLoading } = useQuery({
    queryKey: ['commentTimeline', selectedVideo],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_comment_timeline', {
        video_filter: selectedVideo === 'all' ? null : selectedVideo
      });

      if (error) {
        console.error('Error fetching timeline:', error);
        throw error;
      }

      // Filter to last 3 months
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      
      const filteredData = (data || []).filter(point => 
        new Date(point.time_bucket) >= threeMonthsAgo
      );

      return filteredData;
    },
    refetchInterval: 30000
  });

  const chartData = {
    datasets: [
      {
        label: 'Daily Comments',
        data: timelineData?.map(point => ({
          x: new Date(point.time_bucket),
          y: point.comment_count
        })) || [],
        borderColor: CHART_STYLES.colors.primary,
        backgroundColor: CHART_STYLES.colors.primaryAlpha,
        fill: true,
        tension: 0.1,
        pointRadius: 4,
        pointHoverRadius: 8,
        borderWidth: 2,
        pointBackgroundColor: CHART_STYLES.colors.primary,
        pointBorderColor: '#FFFFFF',
        pointBorderWidth: 1.5
      }
    ]
  };

  return (
    <Card className="col-span-4 bg-zinc-800/20 border-zinc-700">
      <CardHeader>
        <CardTitle className="text-white">Comments Over Time</CardTitle>
      </CardHeader>
      <CardContent className="pl-2">
        <div className="h-[350px] relative">
          {isLoading ? (
            <div className="flex items-center justify-center h-full text-zinc-400">
              Loading...
            </div>
          ) : timelineData?.length === 0 ? (
            <div className="flex items-center justify-center h-full text-zinc-400">
              No comment data available
            </div>
          ) : (
            <Line 
              data={chartData} 
              options={{
                ...getChartOptions(),
                plugins: {
                  ...getChartOptions().plugins,
                  tooltip: {
                    ...getChartOptions().plugins.tooltip,
                    backgroundColor: 'rgba(24, 24, 27, 0.9)', // Zinc-900 with opacity
                  }
                },
                scales: {
                  ...getChartOptions().scales,
                  x: {
                    ...getChartOptions().scales.x,
                    grid: {
                      ...getChartOptions().scales.x.grid,
                      color: 'rgba(255, 255, 255, 0.1)'
                    },
                    ticks: {
                      ...getChartOptions().scales.x.ticks,
                      color: 'rgba(255, 255, 255, 0.7)'
                    }
                  },
                  y: {
                    ...getChartOptions().scales.y,
                    grid: {
                      ...getChartOptions().scales.y.grid,
                      color: 'rgba(255, 255, 255, 0.1)'
                    },
                    ticks: {
                      ...getChartOptions().scales.y.ticks,
                      color: 'rgba(255, 255, 255, 0.7)'
                    }
                  }
                }
              }} 
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
